/**
 * ****************************************************************************
 * @description     :   Store for Search Box Component
 * @created at      :   2025/06/15
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import repository from './repository';

/**
 * Highlight keywords in text
 */
function highlightKeywords(text, keyword) {
    if (!text || !keyword) return text;

    // Split keyword by spaces (both regular and fullwidth)
    const keywords = keyword
        .replace(/\u3000/g, ' ') // Replace fullwidth space with regular space
        .split(' ')
        .filter((k) => k.trim().length > 0)
        .map((k) => k.trim());

    if (keywords.length === 0) return text;

    let highlightedText = text;

    // Highlight each keyword individually
    keywords.forEach((kw) => {
        const regex = new RegExp(`(${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        highlightedText = highlightedText.replace(regex, '<b>$1</b>');
    });

    return highlightedText;
}

// Default state
const defaultState = {
    // Suggestions list
    suggestions: []
};

/**
 * Store for Search Box
 */
export default {
    // Enable namespaced store module
    namespaced: true,

    // State
    state: { ...defaultState },

    // Mutations
    mutations: {
        /**
         * Set suggestions
         * @param {Object} state - Current state
         * @param {Array} suggestions - Suggestions list
         */
        SET_SUGGESTIONS(state, suggestions) {
            state.suggestions = suggestions;
        }
    },

    // Actions
    actions: {
        async fetchSuggestions({ commit }, { keyword = '' } = {}) {
            try {
                // If keyword is empty or only spaces, clear suggestions without API call
                const trimmedKeyword = keyword.trim();
                if (!trimmedKeyword) {
                    commit('SET_SUGGESTIONS', []);
                    return;
                }

                // Call API via repository with keyword
                const response = await repository.getSuggestions({ keyword });

                if (response && response.data && response.data.Code === 200) {
                    const suggestionsData = response.data.Data || [];

                    // Transform API data to component format
                    const formattedSuggestions = suggestionsData.map((item) => {
                        return {
                            faqId: item.FAQID,
                            title: item.display_message || '',
                            url: item.url || '',
                            keywordId: item.KeyWordID || null,
                            breadcrumb: item.breadcrumb || '',
                            highlightedTitle: highlightKeywords(item.display_message || '', keyword)
                        };
                    });

                    commit('SET_SUGGESTIONS', formattedSuggestions);
                } else {
                    console.error('Error fetching suggestions:', response);
                    // Set empty suggestions in case of error
                    commit('SET_SUGGESTIONS', []);
                }
            } catch (error) {
                console.error('Error fetching suggestions:', error);
                // Set empty suggestions in case of error
                commit('SET_SUGGESTIONS', []);
            }
        },

        async updateKeywordCount(context, { keywordId } = {}) {
            try {
                // Call API via repository to update keyword count
                const response = await repository.updateKeywordCount({
                    keywordId
                });

                if (response && response.data && response.data.Code === 200) {
                    return response.data;
                } else {
                    // Log warning but don't throw error for silent handling
                    console.warn('Keyword count update returned non-success code:', response?.data || response);
                    return (
                        response?.data || {
                            Code: 400,
                            Message: 'Update failed'
                        }
                    );
                }
            } catch (error) {
                // Silent error handling - log but don't throw
                console.warn('Error updating keyword count (silent):', error.message || error);
                return {
                    Code: 500,
                    Message: error.message || 'Network error occurred',
                    Error: error
                };
            }
        }
    }
};
